import * as React from 'react';
import { Field } from '@m/alchemy-ui/Field';
import { Input } from '@m/alchemy-ui/Input';

import {
  Reward,
  RewardTier,
  RewardTierConfiguration
} from 'settings/Settings.types';
import { RewardButton } from 'settings/components/RewardButton';
import { ClearedDates } from 'settings/hooks/useRewards';
import * as Styled from './RewardsPicker.styled';
import { RewardPickerDialog } from './RewardPickerDialog';
import { DateTime, shortUsaTimeOfDayFormat, shortUsaDateFormat } from 'common/utils/date';

function RewardPickerOptionThreshold({ value, onChange }) {
  const handleChange = React.useCallback(
    (ev: React.ChangeEvent<HTMLInputElement>) => {
      const parsedValue = parseInt(ev.target.value, 10);
      const inputValue = Number.isFinite(parsedValue) ? parsedValue : '';
      onChange(inputValue);
    },
    [onChange]
  );

  return (
    <Field
      label="Enter Threshold"
      input={
        <Input type="number" value={value || ''} onChange={handleChange} />
      }
    />
  );
}

function RewardPickerOption({
  reward,
  snappyEnabled,
  multiRegionSnappyRewardsOn,
  showThreshold,
  onSave,
  clearedDate,
}: RewardPickerOptionProps) {
  const [isDialogOpen, setDialogOpen] = React.useState<boolean>(false);
  const openDialog = () => setDialogOpen(true);
  const closeDialog = () => setDialogOpen(false);

  const handleSave = (newRewardConfig: RewardTierConfiguration) => {
    onSave(newRewardConfig);
    closeDialog();
  };

  const handleThresholdSave = (newThresholdValue: number) =>
    onSave({ ...reward, threshold: newThresholdValue });

  const clearedDay = DateTime.fromISO(clearedDate).toFormat(shortUsaDateFormat);
  const clearedTime = DateTime.fromISO(clearedDate).toFormat(shortUsaTimeOfDayFormat);

  return (
    <Styled.RewardPickerOptionContainer>
      <RewardButton
        onClick={openDialog}
        iconUrl={reward.imageUrl}
        text={reward.caption || 'Select Reward'}
        ariaLabel={`Select tier reward: ${reward.tier}`}
      />

      {showThreshold && (
        <>
          <RewardPickerOptionThreshold
            value={reward.threshold}
            onChange={handleThresholdSave}
          />
          {clearedDate && (
            <Styled.ClearedDate>
              Progress last cleared at {clearedDay} at {clearedTime}
            </Styled.ClearedDate>
          )}
        </>
      )}

      <RewardPickerDialog
        reward={reward}
        onSave={handleSave}
        isOpen={isDialogOpen}
        onClose={closeDialog}
        snappyEnabled={snappyEnabled}
        multiRegionSnappyRewardsOn={multiRegionSnappyRewardsOn}
      />
    </Styled.RewardPickerOptionContainer>
  );
}

interface RewardPickerOptionProps {
  reward: RewardTierConfiguration;
  snappyEnabled: boolean;
  multiRegionSnappyRewardsOn: boolean;
  showThreshold: boolean;
  onSave: (newRewardConfig: RewardTierConfiguration) => void;
  clearedDate?: string;
}

export function RewardsPicker({
  value,
  onChange = () => {},
  snappyEnabled = false,
  multiRegionSnappyRewardsOn = false,
  thresholdEnabled = false,
  clearedAtByTier,
}: RewardsPickerProps) {
  const lowTierReward =
    value.find(reward => reward.tier === RewardTier.Low) ||
    ({} as RewardTierConfiguration);
  const midTierReward =
    value.find(reward => reward.tier === RewardTier.Mid) ||
    ({} as RewardTierConfiguration);
  const highTierReward =
    value.find(reward => reward.tier === RewardTier.High) ||
    ({} as RewardTierConfiguration);

  const handleNewRewardConfig = (newReward: Reward) => {
    const newRewards = value.map(reward => {
      return reward.tier === newReward.tier ? newReward : reward;
    });
    onChange(newRewards);
  };

  return (
    <Styled.RewardPickerContainer>
      {[lowTierReward, midTierReward, highTierReward].map(reward => {
        const clearedDate = clearedAtByTier?.[reward.tier.toLowerCase()];
        return (
          <RewardPickerOption
            key={reward.id}
            reward={reward}
            snappyEnabled={snappyEnabled}
            multiRegionSnappyRewardsOn={multiRegionSnappyRewardsOn}
            onSave={handleNewRewardConfig}
            showThreshold={thresholdEnabled}
            clearedDate={clearedDate}
          />
        );
      })}
    </Styled.RewardPickerContainer>
  );
}

interface RewardsPickerProps {
  value: RewardTierConfiguration[];
  snappyEnabled?: boolean;
  multiRegionSnappyRewardsOn?: boolean;
  thresholdEnabled?: boolean;
  onChange?: (newRewards: Reward[]) => void;
  clearedAtByTier?: ClearedDates;
}
